import * as React from 'react'
import { useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { cn } from 'lib/utils'
import PopupCloseButton from 'components/PopupCloseButton'
import { blankNoAutofillFields } from 'lib/noAutofill'

interface DialogProps {
	open: boolean
	onClose: () => void
	children: React.ReactNode
}

/**
 * Carries `onClose` down to `DialogContent`, which renders the close button.
 *
 * `Dialog` owns the handler and `DialogContent` is where the button has to
 * live, so one of them has to reach the other. Context rather than a required
 * prop, because every one of the ~79 call sites would otherwise have to pass
 * it and the ones that forgot would silently lose their close button, which is
 * the bug this is fixing.
 */
const DialogCloseContext = React.createContext<(() => void) | null>(null)

const Dialog = ({ open, onClose, children }: DialogProps) => {
	const rootRef = React.useRef<HTMLDivElement>(null)

	/**
	 * Every way out of the dialog goes through here.
	 *
	 * Closing unmounts the subtree, and Chromium reads a filled address form
	 * being removed from the DOM as a submitted one, which is what pops its
	 * "Update address?" bubble over our dialogs. Emptying the opted-out fields
	 * first leaves nothing for it to offer. Fields not marked with
	 * `noAutofillProps` are untouched, so this is inert for the ~79 surfaces
	 * that have no address on them.
	 */
	const handleClose = useCallback(() => {
		blankNoAutofillFields(rootRef.current)
		onClose()
	}, [onClose])

	const handleEscape = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape') handleClose()
		},
		[handleClose],
	)

	useEffect(() => {
		if (open) {
			document.addEventListener('keydown', handleEscape)
			document.body.style.overflow = 'hidden'
		}
		return () => {
			document.removeEventListener('keydown', handleEscape)
			document.body.style.overflow = ''
		}
	}, [open, handleEscape])

	if (!open) return null

	return ReactDOM.createPortal(
		<div
			ref={rootRef}
			role="dialog"
			aria-modal="true"
			className="fixed inset-0 z-[10002] flex items-center justify-center"
		>
			{/* Backdrop */}
			<div
				className="absolute inset-0 bg-black/50 transition-opacity duration-200"
				onClick={handleClose}
			/>
			{/* Content */}
			<div className="relative z-10 w-full flex justify-center pointer-events-none">
				<DialogCloseContext.Provider value={handleClose}>{children}</DialogCloseContext.Provider>
			</div>
		</div>,
		document.body,
	)
}
Dialog.displayName = 'Dialog'

interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
	/**
	 * Suppress the default close button.
	 *
	 * For a surface that renders its own, and for a confirmation the user
	 * should have to answer rather than wave away. Both are rare: the default
	 * is a way out, because a modal without one reads as a trap on a phone,
	 * where there is no Escape key and the backdrop can be a sliver of screen.
	 */
	hideClose?: boolean
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
	({ className, children, hideClose, ...props }, ref) => {
		const onClose = React.useContext(DialogCloseContext)

		return (
			<div
				ref={ref}
				className={cn(
					// `relative` so the close button below anchors here. Without it an
					// absolutely positioned child anchors to the VIEWPORT, which is
					// the gotcha MYD-394 hit on the map popup.
					// `rounded-2xl` matches the Chat popup, which is the app's popup
					// standard (MYD-529). It holds on a scrolling dialog because
					// `overflow-y-auto` clips the sticky header and footer to it.
					'relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto pointer-events-auto',
					className,
				)}
				{...props}
			>
				{!hideClose && onClose && (
					/*
						Sticky and ZERO HEIGHT, which is doing two jobs.

						Sticky, because this element is the scroll container: an
						absolutely positioned button would scroll away on a long
						dialog, leaving no way out exactly when the content is long
						enough to need one.

						Zero height, because ~79 surfaces already exist and a button
						that occupied a row would push every one of their layouts down.
						This costs no vertical space at all.
					*/
					<div className="sticky top-0 z-20 h-0">
						<PopupCloseButton onClose={onClose} className="absolute right-1 top-1" />
					</div>
				)}
				{children}
			</div>
		)
	},
)
DialogContent.displayName = 'DialogContent'

const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => (
		/*
			`pr-14` leaves room for the close button, which sits at the top right
			of every DialogContent by default. Without it a long title runs
			underneath the button, which looks broken and makes the title hard to
			read at exactly the point the user is trying to work out where they
			are. The extra padding is on the RIGHT only, so nothing shifts.
		*/
		<div ref={ref} className={cn('px-5 py-4 pr-14 border-b border-gray-200', className)} {...props} />
	),
)
DialogHeader.displayName = 'DialogHeader'

const DialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
	({ className, ...props }, ref) => (
		<h2 ref={ref} className={cn('text-lg font-semibold text-gray-900', className)} {...props} />
	),
)
DialogTitle.displayName = 'DialogTitle'

const DialogDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p ref={ref} className={cn('text-sm text-gray-500 mt-1', className)} {...props} />
))
DialogDescription.displayName = 'DialogDescription'

const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => (
		<div
			ref={ref}
			className={cn('px-5 py-4 border-t border-gray-200 flex justify-end gap-2', className)}
			{...props}
		/>
	),
)
DialogFooter.displayName = 'DialogFooter'

export { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter }
